import React, { useReducer, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import SignatureSignIcon from '@mui/icons-material/BorderColorTwoTone';
import SignatureViewIcon from '@mui/icons-material/PageviewTwoTone';
import DateDisplay from '../DateDisplay/DateDisplay';
import AutoComplete from '../AutoCompleteField/AutoCompleteField';
import DateSelect from '../DateSelect/DateSelect';
import TextField from '../TextField/TextField';
import ModalDialog from '../Dialog/ModalDialog';
import Button from '../Buttons/Button';
import isEmpty from '../../../is-empty';
import { encryptString } from '../../../helpers/securityHelper';
import ValueHelper from '../../../helpers/valueHelper';
import { isPersistedId } from '../../../helpers/idHelper';

const sx = {
    signIcon: { color: 'primary.main', fontSize: '0.9em' },
    signIconDisabled: { color: 'grey.500', fontSize: '0.9em' },
    viewIcon: { color: 'secondary.main', fontSize: '0.9em' },
    sigBox: { border: '1px solid #9E9E9E', marginTop: '16px' },
    bold: { fontWeight: 'bold' },
    italic: { fontStyle: 'italic' },
};

const initialFormState = {
    user: '',
    userSelected: null,
    date: new Date(),
    pinSign: false,
    signatureImage: null,
};

const FORM = {
    SET_USER: 'SET_USER',
    SET_DATE: 'SET_DATE',
    SET_PIN_SIGN: 'SET_PIN_SIGN',
    SET_SIGNATURE_IMAGE: 'SET_SIGNATURE_IMAGE',
    HYDRATE: 'HYDRATE',
};

function formReducer(state, action) {
    switch (action.type) {
        case FORM.SET_USER:
            return { ...state, userSelected: action.userSelected, user: action.user };
        case FORM.SET_DATE:
            return { ...state, date: action.value };
        case FORM.SET_PIN_SIGN:
            return { ...state, pinSign: action.value };
        case FORM.SET_SIGNATURE_IMAGE:
            return { ...state, signatureImage: action.value };
        case FORM.HYDRATE:
            return { ...state, ...action.payload };
        default:
            return state;
    }
}

const Signature = (props) => {
    const {
        signature, users: usersProp, user: userProp, object, objectType, type,
        title, name, wasteWitness, disabled, complete, validate, saveCallback, setId,
        onSaveSuccess,
    } = props;

    const { t } = useTranslation();
    const reduxDispatch = useDispatch();
    const auth = useSelector(s => s.auth);
    const constants = useSelector(s => s.auth.constants);

    const [form, dispatchForm] = useReducer(formReducer, initialFormState);
    const [showSignature, setShowSignature] = useState(false);
    const [errors, setErrors] = useState({});
    const [pin, setPin] = useState('');
    const [enableSave, setEnableSave] = useState(true);
    const sigPadRef = useRef(null);

    const filteredUsers = useMemo(
        () => (usersProp ?? []).filter(u => u != null && u.nonBillable !== true),
        [usersProp],
    );

    const isSigned = signature != null && signature !== '' && signature._id !== '' && signature.user != null;

    // Hydrate signature image when viewing an existing signature
    useEffect(() => {
        if (!isSigned) return undefined;
        let cancelled = false;
        (async () => {
            const sigUserId = signature.user?._id;
            const sigRes = sigUserId != null ? await ValueHelper.getUserSignature(sigUserId) : null;
            if (cancelled) return;
            dispatchForm({
                type: FORM.HYDRATE,
                payload: {
                    user: userProp ?? '',
                    date: signature.date != null && signature.date !== '' ? signature.date : new Date(),
                    pinSign: false,
                    signatureImage: sigRes != null ? sigRes : signature.signature ?? null,
                },
            });
        })();
        return () => { cancelled = true; };
    }, [isSigned, signature, userProp]);

    // Hydrate sign-form when no signature yet
    useEffect(() => {
        if (isSigned) return undefined;
        let cancelled = false;
        (async () => {
            let selected = filteredUsers.find(x => x.value === userProp);
            let resolvedUser = userProp;
            if (!selected && filteredUsers.length === 1) {
                selected = filteredUsers[0];
                resolvedUser = selected.value;
            }
            let pinSign = false;
            // Tour mocks supply simulated users whose ids aren't ObjectIds.
            if (selected && isPersistedId(selected.value)) {
                const res = await axios.get('/api/userhaspin/' + selected.value);
                pinSign = res.data?.hasPin === true;
            }
            const sigRes = resolvedUser != null && resolvedUser !== ''
                ? await ValueHelper.getUserSignature(resolvedUser)
                : null;
            if (cancelled) return;
            dispatchForm({
                type: FORM.HYDRATE,
                payload: {
                    user: resolvedUser ?? '',
                    userSelected: selected ?? null,
                    pinSign,
                    signatureImage: sigRes,
                    date: new Date(),
                },
            });
        })();
        return () => { cancelled = true; };
    }, [isSigned, filteredUsers, userProp]);

    const handleUserChange = useCallback(async (_e, value) => {
        dispatchForm({ type: FORM.SET_USER, userSelected: value, user: value?.value ?? null });
        if (value == null) {
            dispatchForm({ type: FORM.SET_PIN_SIGN, value: false });
            return;
        }
        try {
            const res = await axios.get('/api/userhaspin/' + value.value);
            dispatchForm({ type: FORM.SET_PIN_SIGN, value: res.data?.hasPin === true });
        } catch {
            dispatchForm({ type: FORM.SET_PIN_SIGN, value: false });
        }
    }, []);

    const handlePinChange = useCallback((e) => {
        const value = e.target.value;
        if (isNaN(value)) return;
        setPin(value);
    }, []);

    const handleDateChange = useCallback((value) => {
        dispatchForm({ type: FORM.SET_DATE, value });
    }, []);

    const clearSig = useCallback(() => {
        if (!form.pinSign && sigPadRef.current) sigPadRef.current.clear();
    }, [form.pinSign]);

    const toggleDialog = useCallback(() => {
        setShowSignature(prev => {
            if (prev) {
                setPin('');
                setErrors({});
            }
            return !prev;
        });
    }, []);

    const validateSignature = useCallback(() => {
        const err = {};
        if (form.user == null || form.user === '' || form.userSelected == null) err[t('user')] = t('required');
        if (form.pinSign && (pin == null || pin === '')) err[t('pin')] = t('required');
        if (form.date == null || form.date === '') dispatchForm({ type: FORM.SET_DATE, value: new Date() });
        setErrors(err);
        return isEmpty(err);
    }, [form.user, form.userSelected, form.pinSign, form.date, pin, t]);

    const saveSig = useCallback(async () => {
        if (!enableSave) return;
        setEnableSave(false);

        if (!validateSignature()) {
            setEnableSave(true);
            return;
        }
        if (validate != null) {
            const err = validate();
            if (err != null && err !== '') {
                setEnableSave(true);
                reduxDispatch({ type: 'CREATE_ALERT', payload: { message: err, title: t('error'), severity: 'error' } });
                return;
            }
        }

        let signaturePayload = null;
        let signatureDocument = null;
        if (form.pinSign) {
            const pinResult = await axios.post('/api/userpin', { userid: form.user, pin: encryptString(pin) });
            if (pinResult.data.signatureDocument == null || pinResult.data.expired) {
                setErrors({ [t('pin')]: pinResult.data.expired ? t('pinExpired') : t('pinInvalid') });
                setEnableSave(true);
                return;
            }
            signatureDocument = pinResult.data.signatureDocument !== '' ? pinResult.data.signatureDocument : null;
        } else {
            signaturePayload = sigPadRef.current?.getTrimmedCanvas().toDataURL('image/png') ?? null;
        }

        if (signatureDocument == null && (signaturePayload == null || signaturePayload === '')) {
            setErrors({ [t('signature')]: t('required') });
            setEnableSave(true);
            return;
        }

        const data = {
            _id: signature?._id || null,
            signature: signaturePayload,
            user: form.user,
            object,
            objectType,
            type,
            date: form.date,
            complete,
        };

        if (saveCallback != null) {
            saveCallback(data);
            setShowSignature(false);
            setEnableSave(true);
            return;
        }

        try {
            const result = await axios.post('/api/signature/save', data);
            setId(result.data.id);
            setEnableSave(true);
            setShowSignature(false);
            reduxDispatch({ type: 'CREATE_ALERT', payload: { message: t('signatureSaved'), title: t('success'), severity: 'success' } });
            if (typeof onSaveSuccess === 'function') {
                onSaveSuccess(result.data);
            }
        } catch (err) {
            setEnableSave(true);
            if (err.response == null) return;
            const respData = err.response.data;
            if (respData?.inputErrors) {
                const errorMap = {};
                const messages = [];
                for (const ie of respData.inputErrors) {
                    const key = ie.type === 'container' ? `${ie.item} - ${ie.container}` : ie.item;
                    const msg = ie.type === 'container'
                        ? `${t('moreThanContainer')} (${t('available')}: ${ie.available}, ${t('requested')}: ${ie.requested})`
                        : `${t('moreThanInventory')} (${t('available')}: ${ie.available}, ${t('requested')}: ${ie.requested})`;
                    errorMap[key] = msg;
                    messages.push(`${key}: ${msg}`);
                }
                reduxDispatch({ type: 'CREATE_ERROR', payload: errorMap });
                reduxDispatch({ type: 'SET_INPUT_ERRORS', payload: respData.inputErrors });
                reduxDispatch({ type: 'CREATE_ALERT', payload: { message: messages.join('\n'), title: t('error'), severity: 'error' } });
            } else {
                reduxDispatch({ type: 'CREATE_ALERT', payload: { message: respData, title: t('error'), severity: 'error' } });
            }
        }
    }, [
        enableSave, validateSignature, validate, form.pinSign, form.user, form.date,
        pin, signature, object, objectType, type, complete, saveCallback, setId, onSaveSuccess, reduxDispatch, t,
    ]);

    const buttonStack = useMemo(() => {
        const stack = [];
        const showClear = (signature == null && signature === '') && form.pinSign === false;
        if (showClear) {
            stack.push(<Button key={`${name}-clearButton`} variant="deleteDialog" onClick={clearSig}>{t('clear')}</Button>);
        }
        stack.push([
            <Button key={`${name}-hideButton`} variant="closeDialog" onClick={toggleDialog}>{t('close')}</Button>,
        ]);
        if (signature == null || signature._id === '') {
            stack.push(
                <Button key={`${name}-saveButton`} variant="save" onClick={saveSig} disabled={!enableSave}>{t('sign')}</Button>,
            );
        }
        return stack;
    }, [name, signature, form.pinSign, enableSave, clearSig, toggleDialog, saveSig, t]);

    const triggerDisabled = disabled && !isSigned;
    const triggerIconSx = isSigned ? sx.viewIcon : (disabled ? sx.signIconDisabled : sx.signIcon);

    return (
        <>
            <IconButton
                edge="end"
                aria-label={`${name}-signature-action`}
                onClick={toggleDialog}
                disabled={triggerDisabled}
            >
                {isSigned ? <SignatureViewIcon sx={triggerIconSx} /> : <SignatureSignIcon sx={triggerIconSx} />}
            </IconButton>
            <ModalDialog
                buttonStack={buttonStack}
                title={title}
                toggleDialog={toggleDialog}
                dialogStatus={showSignature}
                fullWidth
            >
                {isSigned ? (
                    <Grid container>
                        <Grid container size={{ xs: 12, md: 'grow' }}>
                            <Grid size={5}>
                                <Typography variant="body" style={sx.bold}>{t('name')}:</Typography>
                                <Typography variant="body2">
                                    {signature.user.firstName + ' ' + signature.user.lastName}
                                </Typography>
                                <Typography variant="body" style={sx.bold}>{t('date')}:</Typography>
                                <Typography variant="body2">
                                    <DateDisplay format={auth.user.dateFormat + ' hh:mm aa'} value={signature.date} />
                                </Typography>
                            </Grid>
                            <Grid size={7}>
                                {form.signatureImage != null && form.signatureImage !== '' && (
                                    <img src={form.signatureImage} alt={t('noSignature')} height="80%" width="80%" />
                                )}
                            </Grid>
                            {wasteWitness && (
                                <Grid size={12}>
                                    <br />
                                    <Typography variant="body2">{type === constants.signatureTypes.signer ? t('signDestructionApprover') : t('signDestructionWitness')}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <AutoComplete
                                    value={form.userSelected}
                                    options={filteredUsers}
                                    error={errors[t('user')] != null}
                                    getOptionLabel={(option) => option.label}
                                    onChange={handleUserChange}
                                    renderInput={(params) => <TextField {...params} label="User" />}
                                    helperText={errors[t('user')]}
                                    label={t('user')}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <DateSelect
                                    keepTime
                                    label={t('date')}
                                    value={form.date}
                                    onChange={handleDateChange}
                                    helperText={errors[t('date')]}
                                    error={errors[t('date')] != null}
                                    format={auth.user.dateFormat}
                                    fullWidth
                                />
                            </Grid>
                            {form.pinSign && (
                                <Grid size={{ xs: 12, md: 2 }}>
                                    <TextField
                                        value={pin}
                                        onChange={handlePinChange}
                                        name="pin"
                                        size="medium"
                                        fullWidth
                                        label={t('pin')}
                                        type="password"
                                        error={errors[t('pin')] != null}
                                        helperText={errors[t('pin')]}
                                    />
                                </Grid>
                            )}
                        </Grid>
                        {!form.pinSign && (
                            // <Box component="div" borderRadius="borderRadius" sx={sx.sigBox}>
                            //     <SignatureCanvas
                            //         penColor="#5BC6CC"
                            //         canvasProps={{ width: 500, height: 200, className: 'sigCanvas' }}
                            //         ref={(ref) => { sigPadRef.current = ref; }}
                            //     />
                            // </Box>
                            <Box component="div" borderRadius="borderRadius" sx={sx.sigBox}>
                                <Typography variant="body2">
                                    {t('noSignatureAvailable')}
                                </Typography>
                            </Box>
                        )}
                        {wasteWitness && (
                            <Typography variant="body2" style={sx.bold}>
                                <br />{type === constants.signatureTypes.signer ? t('signDestructionApprover') : t('signDestructionWitness')}
                            </Typography>
                        )}
                        <br />
                        <Typography variant="caption" style={sx.italic}>{t('signatureDisclaimer')}</Typography>
                    </>
                )}
            </ModalDialog>
        </>
    );
};

export default Signature;
